import { computed, Injectable, signal } from '@angular/core';
import { CarFilters, CPOs, IMapCpoFilterOption } from 'common_library';
import { HttpIoService } from './communication/http-io.service';
import { MapCpoFilterViewOption } from '../types/map-cpo-filter-view-option.interface';

export const NO_OPERATOR_SELECTED = '__NO_OPERATOR_SELECTED__';

const STATIC_CPO_FILTER_OPTIONS: MapCpoFilterViewOption[] = [
  {
    key: 'operator:ITYFL',
    label: CPOs.YOURFILL,
    cpo: CPOs.YOURFILL,
    operatorIds: ['ITYFL'],
    image: 'assets/stations/yfllogo.svg',
  },
  {
    key: 'operator:ITENX',
    label: CPOs.ENELX,
    cpo: CPOs.ENELX,
    operatorIds: ['ITENX'],
    image: 'assets/stations/enxlogo.svg',
  },
  {
    key: 'operator:ITF2X',
    label: CPOs.FREETOX,
    cpo: CPOs.FREETOX,
    operatorIds: ['ITF2X'],
    image: 'assets/stations/ftxlogo.svg',
  },
  {
    key: 'operator:ITBEC',
    label: 'PLN',
    operatorIds: ['ITBEC'],
    image: 'assets/stations/it_plenitude.svg',
  },
  {
    key: 'operator:ITA2M',
    label: 'A2A',
    operatorIds: ['ITA2M'],
    image: 'assets/stations/it_a2a.svg',
  },
];

@Injectable({
  providedIn: 'root',
})
export class CpoFilterOptionsService {
  private readonly $dynamicOptions = signal<IMapCpoFilterOption[]>([]);

  readonly $options = computed<MapCpoFilterViewOption[]>(() =>
    this.mergeOptions(this.$dynamicOptions()),
  );

  constructor(private HIO: HttpIoService) {}

  async loadOptions(): Promise<MapCpoFilterViewOption[]> {
    try {
      const options =
        await this.HIO.get<IMapCpoFilterOption[]>('company-role/cpo-map-filter-options');
      this.$dynamicOptions.set(options || []);
    } catch (error) {
      this.$dynamicOptions.set([]);
    }

    return this.$options();
  }

  getOptions(): MapCpoFilterViewOption[] {
    return this.$options();
  }

  normalizeFilterState(filters?: Partial<CarFilters>): CarFilters {
    const nextFilters: CarFilters = {
      quick: this.asStringArray(filters?.quick),
      carId: typeof filters?.carId === 'number' ? filters.carId : null,
      plugs: this.asStringArray(filters?.plugs, [
        'Type1',
        'Type2',
        'Type2s',
        'Type3a',
        'Type3c',
        'CHAdeMO',
        'Combo1',
        'Combo2',
      ]),
      range: this.asRange(filters?.range),
      cpos: this.asStringArray(filters?.cpos),
      operatorIds: this.asStringArray(filters?.operatorIds),
    };

    const allSelectable = this.getAllSelectableSelection();
    const allowedCpos = new Set(allSelectable.cpos);
    const allowedOperatorIds = new Set(
      allSelectable.operatorIds.map((value) => this.normalizeOperatorId(value)),
    );
    allowedOperatorIds.add(this.normalizeOperatorId(NO_OPERATOR_SELECTED));

    nextFilters.cpos = this.uniqueValues(nextFilters.cpos).filter((value) =>
      allowedCpos.has(value),
    );
    nextFilters.operatorIds = this.uniqueValues(
      nextFilters.operatorIds,
      (value) => this.normalizeOperatorId(value),
    ).filter((value) =>
      allowedOperatorIds.has(this.normalizeOperatorId(value)),
    );

    const cpoOnlySelection = this.uniqueValues(
      this.getOptions()
        .map((option) => option.cpo)
        .filter((value): value is string => !!value),
    );
    const isLegacyAllSelection =
      nextFilters.operatorIds.length === 0
      && this.sameValues(nextFilters.cpos, cpoOnlySelection);

    if (
      isLegacyAllSelection
      || this.sameValues(nextFilters.cpos, allSelectable.cpos)
        && this.sameValues(
          nextFilters.operatorIds,
          allSelectable.operatorIds,
          (value) => this.normalizeOperatorId(value),
        )
    ) {
      nextFilters.cpos = [];
      nextFilters.operatorIds = [];
    }

    return nextFilters;
  }

  isAllOperatorsSelected(filters?: CarFilters): boolean {
    const normalizedFilters = this.normalizeFilterState(filters);
    return (
      (normalizedFilters.cpos?.length || 0) === 0
      && (normalizedFilters.operatorIds?.length || 0) === 0
    );
  }

  private mergeOptions(dynamicOptions: IMapCpoFilterOption[]): MapCpoFilterViewOption[] {
    const merged = new Map<string, MapCpoFilterViewOption>();
    const staticOrder = new Map<string, number>();

    STATIC_CPO_FILTER_OPTIONS.forEach((option, index) => {
      merged.set(option.key, { ...option });
      staticOrder.set(option.key, index);
    });

    for (const dynamicOption of dynamicOptions || []) {
      const viewOption = this.toViewOption(dynamicOption);
      const targetKey =
        this.findMatchingOptionKey(viewOption, merged) || viewOption.key;
      const currentOption = merged.get(targetKey);
      if (!currentOption) {
        merged.set(targetKey, viewOption);
        continue;
      }

      merged.set(targetKey, {
        ...currentOption,
        label: viewOption.label || currentOption.label,
        image: viewOption.image || currentOption.image,
        cpo: viewOption.cpo || currentOption.cpo,
        operatorIds: this.uniqueValues([
          ...(currentOption.operatorIds || []),
          ...(viewOption.operatorIds || []),
        ], (value) => this.normalizeOperatorId(value)),
      });
    }

    return Array.from(merged.values()).sort((first, second) => {
      const firstStaticOrder = staticOrder.get(first.key);
      const secondStaticOrder = staticOrder.get(second.key);
      if (
        typeof firstStaticOrder === 'number'
        && typeof secondStaticOrder === 'number'
        && firstStaticOrder !== secondStaticOrder
      ) {
        return firstStaticOrder - secondStaticOrder;
      }
      if (typeof firstStaticOrder === 'number' && typeof secondStaticOrder !== 'number') {
        return -1;
      }
      if (typeof firstStaticOrder !== 'number' && typeof secondStaticOrder === 'number') {
        return 1;
      }

      return (first.label || '').localeCompare(second.label || '', undefined, {
        sensitivity: 'base',
      });
    });
  }

  private toViewOption(option: IMapCpoFilterOption): MapCpoFilterViewOption {
    return {
      key: option.key,
      label: option.shortLabel || option.label,
      cpo: option.cpo,
      operatorIds: [...(option.operatorIds || [])],
      image: option.assetName
        ? this.HIO.endPoint(`multimedia/image/${option.assetName}`)
        : undefined,
    };
  }

  private findMatchingOptionKey(
    option: MapCpoFilterViewOption,
    optionsMap: Map<string, MapCpoFilterViewOption>,
  ): string | null {
    if (optionsMap.has(option.key)) {
      return option.key;
    }

    const normalizedOperatorIds = (option.operatorIds || []).map((value) =>
      this.normalizeOperatorId(value),
    );

    for (const [key, existingOption] of optionsMap.entries()) {
      const hasSameCpo =
        !!option.cpo
        && !!existingOption.cpo
        && option.cpo === existingOption.cpo;
      const hasSameOperatorId = (existingOption.operatorIds || []).some((value) =>
        normalizedOperatorIds.includes(this.normalizeOperatorId(value)),
      );
      if (hasSameCpo || hasSameOperatorId) {
        return key;
      }
    }

    return null;
  }

  private getAllSelectableSelection(): {
    cpos: string[];
    operatorIds: string[];
  } {
    return {
      cpos: this.uniqueValues(
        this.getOptions()
          .map((option) => option.cpo)
          .filter((value): value is string => !!value),
      ),
      operatorIds: this.uniqueValues(
        this.getOptions().reduce<string[]>((values, option) => {
          values.push(...(option.operatorIds || []));
          return values;
        }, []),
        (value) => this.normalizeOperatorId(value),
      ),
    };
  }

  private uniqueValues(
    values: string[],
    normalizer: (value: string) => string = (entry) => entry,
  ): string[] {
    const seen = new Set<string>();
    return values.filter((value) => {
      const normalizedValue = normalizer(value);
      if (seen.has(normalizedValue)) {
        return false;
      }
      seen.add(normalizedValue);
      return true;
    });
  }

  private sameValues(
    first: string[] = [],
    second: string[] = [],
    normalizer: (value: string) => string = (entry) => entry,
  ): boolean {
    if (first.length !== second.length) {
      return false;
    }

    const firstSet = new Set(first.map((value) => normalizer(value)));
    const secondSet = new Set(second.map((value) => normalizer(value)));
    if (firstSet.size !== secondSet.size) {
      return false;
    }

    return Array.from(firstSet).every((value) => secondSet.has(value));
  }

  private normalizeOperatorId(value?: string): string {
    return (value || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  }

  private asStringArray(value: unknown, fallback: string[] = []): string[] {
    if (!Array.isArray(value)) {
      return [...fallback];
    }

    return value.filter((entry): entry is string => typeof entry === 'string');
  }

  private asRange(value: unknown): { lower: number; upper: number } {
    if (
      value
      && typeof value === 'object'
      && typeof (value as { lower?: unknown }).lower === 'number'
      && typeof (value as { upper?: unknown }).upper === 'number'
    ) {
      return {
        lower: (value as { lower: number }).lower,
        upper: (value as { upper: number }).upper,
      };
    }

    return { lower: 2, upper: 350 };
  }
}
